// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {object} MediaGalleryRefs
 * @property {HTMLElement} viewerViewport
 * @property {HTMLElement} viewerContainer
 * @property {HTMLElement[]} [viewerSlides]
 * @property {HTMLElement | undefined} thumbnailViewport
 * @property {HTMLElement | undefined} thumbnailContainer
 * @property {HTMLButtonElement[]} [thumbnailButtons]
 * @property {HTMLButtonElement[]} [paginationDots]
 * @property {HTMLButtonElement[]} [zoomButtons]
 * @property {HTMLElement | undefined} liveRegion
 */

/** @type {Map<string, Promise<void>>} */
const lightboxStylesheets = new Map();

/**
 * Media gallery with synced thumbnails powered by Embla Carousel.
 *
 * @extends Component<MediaGalleryRefs>
 */
class MediaGalleryComponent extends Component {
  /** @type {any} */
  #emblaMain;
  /** @type {AbortController | undefined} */
  #variantAbortController;
  /** @type {{ x: number, y: number } | undefined} */
  #pointerDownPosition;
  /** @type {AbortController | undefined} */
  #pointerAbortController;

  connectedCallback() {
    super.connectedCallback();
    this.#initEmbla();
    this.#registerVariantEvents();
    this.#pointerAbortController = new AbortController();
    this.addEventListener(
      'pointerdown',
      (event) => {
        this.#pointerDownPosition = { x: event.clientX, y: event.clientY };
      },
      { signal: this.#pointerAbortController.signal }
    );

    requestIdleCallback(() => {
      const slides = this.refs.viewerSlides ?? [];
      const activeIndex = slides.findIndex((slide) => slide.classList.contains('media-gallery__slide--active'));
      this.#preloadAdjacentSlides(Math.max(activeIndex, 0));
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroyEmbla();
    this.#initEmbla();
    this.#registerVariantEvents();
  }

  disconnectedCallback() {
    this.#destroyEmbla();
    this.#variantAbortController?.abort();
    this.#pointerAbortController?.abort();
    super.disconnectedCallback();
  }

  /**
   * Handles thumbnail click events.
   * @param {{ index?: number }} data
   */
  handleThumbnailClick(data) {
    const index = Number(data?.index);
    if (!Number.isNaN(index)) {
      this.#scrollToIndex(index);
    }
  }

  /**
   * Handles clicks on a slide's zoom button and opens the lightbox.
   * Ignores clicks that conclude an Embla drag gesture.
   * @param {{ index?: number }} data
   * @param {MouseEvent} event
   */
  handleZoomClick(data, event) {
    const index = Number(data?.index);
    if (Number.isNaN(index)) return;

    if (event.detail > 0 && this.#pointerDownPosition) {
      const distance = Math.hypot(
        event.clientX - this.#pointerDownPosition.x,
        event.clientY - this.#pointerDownPosition.y
      );
      if (distance > 10) return;
    }

    this.#openLightbox(index);
  }

  /**
   * Lazily loads PhotoSwipe and opens it at the given slide index.
   * @param {number} index
   */
  async #openLightbox(index) {
    const slides = this.refs.viewerSlides ?? [];
    if (!slides.length) return;

    const [{ default: PhotoSwipe }] = await Promise.all([import('photoswipe'), this.#loadLightboxStyles()]);

    const dataSource = slides.map((slide) => {
      const image = slide.querySelector('img');
      return {
        src: slide.dataset.zoomSrc || image?.currentSrc || '',
        msrc: image?.currentSrc || undefined,
        width: Number(slide.dataset.zoomWidth) || image?.naturalWidth || 0,
        height: Number(slide.dataset.zoomHeight) || image?.naturalHeight || 0,
        alt: image?.alt || '',
        element: slide,
      };
    });

    /** @type {import('photoswipe').PhotoSwipeOptions} */
    const options = {
      dataSource,
      index,
      showHideAnimationType: 'zoom',
    };
    if (this.dataset.lightboxCloseLabel) options.closeTitle = this.dataset.lightboxCloseLabel;
    if (this.dataset.lightboxZoomLabel) options.zoomTitle = this.dataset.lightboxZoomLabel;
    if (this.dataset.lightboxPrevLabel) options.arrowPrevTitle = this.dataset.lightboxPrevLabel;
    if (this.dataset.lightboxNextLabel) options.arrowNextTitle = this.dataset.lightboxNextLabel;

    const lightbox = new PhotoSwipe(options);
    lightbox.on('change', () => {
      this.#scrollToIndex(lightbox.currIndex, { animate: false });
    });
    lightbox.init();
  }

  /**
   * Injects the PhotoSwipe stylesheet once and resolves when it is loaded.
   * @returns {Promise<void>}
   */
  #loadLightboxStyles() {
    const href = this.dataset.photoswipeCss;
    if (!href) return Promise.resolve();

    let promise = lightboxStylesheets.get(href);
    if (!promise) {
      promise = new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () => resolve();
        document.head.appendChild(link);
      });
      lightboxStylesheets.set(href, promise);
    }
    return promise;
  }

  /**
   * Sets the active media by DOM id.
   * @param {string} mediaDomId
   * @param {{ animate?: boolean }} [options]
   */
  setActiveMedia(mediaDomId, options = {}) {
    const slides = this.refs.viewerSlides ?? [];
    const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaDomId);
    if (index < 0) return;

    if (this.#emblaMain) {
      const jump = options.animate === false;
      this.#emblaMain.scrollTo(index, jump);
    } else {
      this.#setActiveClasses(index);
      this.#announceSlide(index);
    }
  }

  /**
   * Registers listeners for variant updates so the gallery syncs to the featured media.
   */
  #registerVariantEvents() {
    this.#variantAbortController?.abort();
    this.#variantAbortController = new AbortController();
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#variantAbortController.signal,
    });
  }

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!event.detail?.resource?.featured_media?.id) return;
    const mediaDomId = `${this.dataset.sectionId || this.dataset.section}-${event.detail.resource.featured_media.id}`;
    this.setActiveMedia(mediaDomId);
  };

  #initEmbla() {
    const viewport = this.refs.viewerViewport;
    if (!viewport) return;

    const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
    if (typeof EmblaCarousel !== 'function') {
      requestIdleCallback(() => this.#initEmbla());
      return;
    }

    this.#emblaMain = EmblaCarousel(viewport, {
      align: 'start',
      loop: this.dataset.loop === 'true',
      containScroll: 'trimSnaps',
    });
    this.#emblaMain.on('select', this.#handleMainSelect);
    this.#emblaMain.on('reInit', this.#handleMainSelect);

    const initialMediaId = this.dataset.initialMediaId;
    if (initialMediaId) {
      this.setActiveMedia(initialMediaId, { animate: false });
    } else {
      this.#handleMainSelect();
    }
  }

  #destroyEmbla() {
    this.#emblaMain?.destroy();
    this.#emblaMain = undefined;
  }

  #handleMainSelect = () => {
    const selectedIndex = this.#emblaMain?.selectedScrollSnap?.() ?? 0;
    this.#setActiveClasses(selectedIndex);
    this.#announceSlide(selectedIndex);
    this.#scrollThumbnailIntoView(selectedIndex);
    this.#preloadAdjacentSlides(selectedIndex);
  };

  /**
   * Eagerly loads the images adjacent to the given slide index so they are
   * already available when the user swipes to them.
   * @param {number} index
   */
  #preloadAdjacentSlides(index) {
    const slides = this.refs.viewerSlides ?? [];
    if (slides.length < 2) return;

    const loop = this.dataset.loop === 'true';

    for (let neighbor of [index + 1, index - 1]) {
      if (loop) {
        neighbor = (neighbor + slides.length) % slides.length;
      }
      const image = slides[neighbor]?.querySelector('img');
      if (image && image.loading === 'lazy') {
        image.loading = 'eager';
      }
    }
  }

  /**
   * @param {number} index
   */
  #setActiveClasses(index) {
    const slides = this.refs.viewerSlides ?? [];
    const thumbs = this.refs.thumbnailButtons ?? [];
    const dots = this.refs.paginationDots ?? [];
    const zoomButtons = this.refs.zoomButtons ?? [];

    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('media-gallery__slide--active', slideIndex === index);
    });

    zoomButtons.forEach((button, buttonIndex) => {
      button.setAttribute('tabindex', buttonIndex === index ? '0' : '-1');
    });

    thumbs.forEach((button, thumbIndex) => {
      const isActive = thumbIndex === index;
      button.classList.toggle('media-gallery__thumb--active', isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('media-gallery__dot--active', dotIndex === index);
    });
  }

  /**
   * @param {number} index
   */
  #announceSlide(index) {
    const liveRegion = this.refs.liveRegion;
    if (!liveRegion) return;
    const total = Number(this.dataset.mediaCount || this.refs.viewerSlides?.length || 0);
    const template = this.dataset.liveRegionText || 'Image [index] of [count]';
    liveRegion.textContent = template.replace('[index]', String(index + 1)).replace('[count]', String(total || 0));
  }

  /**
   * Scrolls to a specific slide index.
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #scrollToIndex(index, options = {}) {
    if (this.#emblaMain) {
      this.#emblaMain.scrollTo(index, options.animate === false);
    } else {
      this.#setActiveClasses(index);
      this.#announceSlide(index);
    }
  }

  /**
   * Scrolls the thumbnail into view if it's not visible.
   * @param {number} index
   */
  #scrollThumbnailIntoView(index) {
    const thumbs = this.refs.thumbnailButtons ?? [];
    const thumb = thumbs[index];
    const viewport = this.refs.thumbnailViewport;
    if (!thumb || !viewport) return;

    const thumbRect = thumb.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();

    const isAboveViewport = thumbRect.top < viewportRect.top;
    const isBelowViewport = thumbRect.bottom > viewportRect.bottom;

    if (isAboveViewport || isBelowViewport) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}

if (!customElements.get('media-gallery-component')) {
  customElements.define('media-gallery-component', MediaGalleryComponent);
}


